import {AccountData, AccountDataStatusEnum, Portfolio} from '../../api-client-kt/src';
import {FormulaEvaluateTableColumn, FormulaEvaluateTableInput} from '../../api-client-py/src';
import {groupBy, uniq, uniqBy} from 'es-toolkit';
import {EditableComment} from '../components/cell-comment-editor/cell-comment-editor.component';

export interface CheckEvaluation {
    id: string;
    name: string;
    severity: CheckSeverity;
    fields: string[];
}

export interface CellComment extends EditableComment {
    id: string;
    field: string;
    createdBy: string;
    createdAt: Date;
    content: string;
}

export const CheckSeverity = {
    ok: "ok",
    warning: "warning",
    error: "error"
} as const;
export type CheckSeverity = typeof CheckSeverity[keyof typeof CheckSeverity];

interface ChecksDictionaryItem {
    id: string;
    name: string;
    fields: string[];
    severity: CheckSeverity;
}

const severityOrder = {
    [CheckSeverity.ok]: 0,
    [CheckSeverity.warning]: 1,
    [CheckSeverity.error]: 2,
};

export class CheckResults {

    readonly checks: ChecksDictionaryItem [];

    constructor(private dictionary: FormulaEvaluateTableInput) {
        const idIndex = CheckResults.getIndex(dictionary.columns, 'id');
        const nameIndex = CheckResults.getIndex(dictionary.columns, 'name');
        const fieldsIndex = CheckResults.getIndex(dictionary.columns, 'fields');
        const severityIndex = CheckResults.getIndex(dictionary.columns, 'severity');

        this.checks = dictionary.data.map(row => {
            const id = row[idIndex] as string;
            const name = row[nameIndex] as string;
            const fields = (row[fieldsIndex] as string).split(',').map(f => f.trim());
            const severity = row[severityIndex] as CheckSeverity;
            return {id, name, fields, severity};
        });
    }

    private static getIndex(cols: FormulaEvaluateTableColumn[], columnName: string): number {
        const index = cols.findIndex(c => c.name === columnName);
        if (index === -1) throw new Error(`'${columnName}' column not found`);
        return index;
    }
}


export class CheckUtils {

    static asFontAwesomeIconClass(severity: CheckSeverity | null): string {
        switch (severity) {
            case CheckSeverity.ok:
                return 'fa-info-circle';
            case CheckSeverity.warning:
                return 'fa-exclamation-triangle';
            case CheckSeverity.error:
                return 'fa-times-circle';
            default:
                return 'fa-question-circle'; // Fallback for null or unknown severity
        }
    }

    static asFontAwesomeIconElement(severity: CheckSeverity | null | undefined): string {
        if (severity == null) return '';
        else return `<i class="far ${CheckUtils.asFontAwesomeIconClass(severity)}"></i>`;
    }

    static accountStatusAsFontAwesomeIconClass(status: AccountDataStatusEnum | null): string {
        switch (status) {
            case AccountDataStatusEnum.Open: return 'fa-clipboard-list';
            case AccountDataStatusEnum.Completed: return 'fa-check-circle';
            case AccountDataStatusEnum.NeedsReview: return 'fa-message-question';
            default: return 'fa-question-circle'; // Fallback for null or unknown severity
        }
    }

    static accountStatusAsIcon(accountStatus: AccountDataStatusEnum | undefined | null): string {
        if (accountStatus == null) return '';
        else return `<i class="fa-light ${CheckUtils.accountStatusAsFontAwesomeIconClass(accountStatus)}"></i>`;
    }

    static compareSeverity(a: CheckSeverity | null, b: CheckSeverity | null): number {
        if (a === b) return 0;
        if (a == null) return 1; // null is considered less severe
        if (b == null) return -1; // null is considered less severe
        return (severityOrder[a] ?? 0) - (severityOrder[b] ?? 0);
    }

    static max(... values: (CheckSeverity | undefined | null)[]): CheckSeverity | undefined | null {
        return values.reduce((max, current) => {
            if (max == null) return current;
            if (current == null) return max;
            return this.compareSeverity(max, current) >= 0 ? max : current;
        }, undefined as (CheckSeverity | undefined | null));
    }

    static getAccountChecks(account: AccountDataWithChecks): CheckEvaluation[] {
        return account.metadataChecks as CheckEvaluation[] ?? [];
    }

    static getChecksForAttribute(attribute: string, checks: CheckEvaluation[] | undefined): CheckEvaluation[] {
        if (!checks || checks.length === 0) return [];
        else return checks.filter(check => check.fields.includes(attribute));
    }

    static getAllComments(accountData: AccountData): CellComment[] {
        return accountData.metadataComments as CellComment[] ?? [];
    }

    static getAllCommentsGroupedByAttribute(accountData: AccountData): Record<string, CellComment[]> {
        return groupBy(CheckUtils.getAllComments(accountData), comment => comment.field);
    }

    static getCommentsForAttribute(accountData: AccountData, attribute: string): CellComment[] {
        const comments = CheckUtils.getAllComments(accountData);
        return comments.filter(comment => comment.field === attribute);
    }

    static rewriteCommentsForAttribute(accountData: AccountData, attribute: string, newComments: CellComment[]): CellComment[] {
        const allComments = CheckUtils.getAllComments(accountData);
        const attributeComments = newComments.map(c => ({...c, field: attribute}));
        const otherComments = allComments.filter(c => c.field !== attribute);
        return [...attributeComments, ...otherComments];
    }

    static getDistinctErrorCodes(accounts: AccountDataWithChecks[]): string[] {
        const allErrorIds = accounts.flatMap( a => {
            const checks = CheckUtils.getAccountChecks(a);
            return checks.map(c => c.id);
        });
        return uniq(allErrorIds);
    }
}

export interface AccountDataWithChecks extends AccountData {
    errorLevel?: CheckSeverity | null;
}
